import BaseApiService from "./BaseApiService";

/**
 * Billing API Service
 *
 * Handles:
 * - Starting Stripe Checkout Sessions
 * - Opening Stripe Billing Portal
 * - (Optional) verifying a checkout session on success
 *
 * @class BillingApiService
 * @extends BaseApiService
 */
class BillingApiService extends BaseApiService {
  /**
   * Starts a Checkout Session for a productKey.
   * Backend performs preflight (DB + Stripe) and returns { url } or 409 + { portalUrl }.
   * @param {string} productKey - 'physio_user' | 'corporate' | 'preventive_course' | 'physio'
   * @returns {Promise<{url?: string, portalUrl?: string}>}
   */
  async startCheckout(productKey) {
    if (productKey === "physio_user") {
      // Call the new trial endpoint
      const data = await this.post("/checkout/sessions", { productKey });
      return { url: data.url };
    }

    // Else, normal Stripe flow
    const data = await this.post("/checkout/sessions", { productKey });
    if (data?.url) return data;
    throw new Error("Unexpected response from checkout/sessions");
  }

  /**
   * Creates a Billing Portal session for the current user and returns { url }.
   * @returns {Promise<{url: string}>}
   */
  async openBillingPortal() {
    return await this.post("/billing-portal/sessions");
  }

  /**
   * (Optional) Verify a Checkout Session by id (if you add this endpoint).
   * Useful on /checkout/success to cross-check server state.
   * @param {string} sessionId
   * @returns {Promise<any>}
   */
  async verifyCheckoutSession(sessionId) {
    return await this.get(
      `/checkout/sessions/${encodeURIComponent(sessionId)}`
    );
  }
}

// Singleton export
const billingApiService = new BillingApiService();
export default billingApiService;
